import React, { useState, useEffect, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../axios';

// Purchase.TeamName -> the company heading printed on the PO (same details PiPDF prints)
const COMPANIES = {
  "Miah & Son's": {
    name: 'MIAH & SONS INDUSTRIES LTD.',
    address: 'Holding No. 110/1, Barabo, Gazipur-1703, Bangladesh.',
    mobile: '01711564795, 01866 990700',
    email: 'miahandsons@gmail.com',
    logo: '/Images/miahandsonslogo.png',
  },
  'Tanvir Pack': {
    name: 'Tanvir Printing & Packaging IND.',
    address: '746/747 NOLVOG, DHORANGERTEK, TURAG, UTTARA, DHAKA-1230',
    mobile: '01711564795, 01866 990700',
    email: 'tanverpack@yahoo.com',
  },
  ProPack: {
    name: 'Propack Trims & Accessories Industries',
    address: 'Muradpur, Shakaswar, Kaliakoir, Gazipur-1703, Bangladesh.',
    mobile: '+1 (555) 123-4567',
    email: 'contact@propackusa.com',
  },
};

const PAD_GAP = '2.25in';
const padKey = 'purchaseOrderPad';

const bdDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};
const fmt = (n, digits = 2) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const qtyText = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
const isTons = (unit) => /^(mt|ton|tons|tonne|tonnes|t)$/i.test(String(unit || '').trim());
const isKg = (unit) => /^(kg|kgs)$/i.test(String(unit || '').trim());

const cell = { border: '1px solid #000', padding: '5px 8px' };

// Memoised so text typed into the live DOM in edit mode is never overwritten by a re-render.
const FreshPo = memo(function FreshPo({ purchase, lines, pad }) {
  const company = COMPANIES[purchase.TeamName] || { name: purchase.TeamName };
  const isPaper = purchase.PurchaseCategory === 'Direct';
  const isImport = !!purchase.IsForeign || purchase.PurchaseType === 'Import';
  const currency = purchase.Currency || 'BDT';
  const transport = parseFloat(purchase.TransportCost) || 0;

  // Paper rates are per kg (that is how receiving prices the rolls), so the amount is only known
  // when the line is ordered by weight. Other materials are priced per their own unit.
  const rows = lines.map((l) => {
    const item = l.GSMID ? `${l.GSMName} ${l.GSMValue} GSM` : (l.MaterialName || l.Description || '');
    let amount = null;
    if (!isPaper) amount = (parseFloat(l.Qty) || 0) * (parseFloat(l.UnitPrice) || 0);
    else if (isTons(l.Unit)) amount = (parseFloat(l.Qty) || 0) * 1000 * (parseFloat(l.UnitPrice) || 0);
    else if (isKg(l.Unit)) amount = (parseFloat(l.Qty) || 0) * (parseFloat(l.UnitPrice) || 0);
    return { ...l, item, amount };
  });
  const allPriced = rows.every((r) => r.amount !== null);
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const labelSpan = isPaper ? 3 : 2;

  // Quantity totals per unit, e.g. "12 roll, 5 MT"
  const qtyByUnit = {};
  rows.forEach((r) => { qtyByUnit[r.Unit] = (qtyByUnit[r.Unit] || 0) + (parseFloat(r.Qty) || 0); });
  const qtyTotal = Object.entries(qtyByUnit).map(([u, q]) => `${qtyText(q)} ${u}`).join(', ');

  const terms = [
    ['Payment term', purchase.PaymentTerm],
    ['Delivery required by', bdDate(purchase.NeededByDate)],
    ['Expected arrival', bdDate(purchase.ExpectedArrivalDate)],
    ...(isImport ? [['Incoterm', purchase.Incoterm], ['Port of loading', purchase.PortOfLoading], ['Port of discharge', purchase.PortOfDischarge]] : []),
  ].filter(([, v]) => v);

  return (
    <div style={{ color: '#000' }}>
      {!pad && (
        <>
          <table width="100%" cellSpacing="0" cellPadding="0">
            <tbody>
              <tr>
                {company.logo && (
                  <td width="90" valign="top"><img style={{ maxHeight: '70px' }} src={company.logo} alt={company.name} /></td>
                )}
                <td valign="top">
                  <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{company.name}</div>
                  {company.address && <div style={{ fontSize: '13px' }}><strong>Factory Address:</strong> {company.address}</div>}
                  {company.mobile && (
                    <div style={{ fontSize: '13px' }}>
                      <strong>Mobile No.:</strong> {company.mobile}
                      {company.email && <> &nbsp;<strong>Email:</strong> {company.email}</>}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <hr style={{ border: 'none', borderTop: '1px solid #000', margin: '8px 0 12px' }} />
        </>
      )}

      <div style={{ textAlign: 'center', margin: '4px 0 14px' }}>
        <span style={{ fontSize: '20px', fontWeight: 'bold', border: '1px solid #333', padding: '6px 24px' }}>PURCHASE ORDER</span>
      </div>

      <table width="100%" cellSpacing="0" cellPadding="0" style={{ marginBottom: '14px' }}>
        <tbody>
          <tr>
            <td width="60%" valign="top">
              <div>To</div>
              <div><strong>{purchase.VendorName}</strong></div>
              {purchase.VendorAddress && <div>{purchase.VendorAddress}</div>}
              {purchase.ContactPerson && <div>Attn: {purchase.ContactPerson}</div>}
              {(purchase.Phone || purchase.WhatsApp) && <div>Phone: {purchase.Phone || purchase.WhatsApp}</div>}
              {purchase.Email && <div>Email: {purchase.Email}</div>}
            </td>
            <td width="40%" valign="top" align="right">
              <div><strong>PO No:</strong> {purchase.PONo}</div>
              <div><strong>Date:</strong> {bdDate(purchase.PODate)}</div>
              <div><strong>Currency:</strong> {currency}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginBottom: '10px' }}>Dear Sir, please supply the following items as per the terms below.</div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...cell, width: '6%', textAlign: 'center' }}>SL</th>
            <th style={{ ...cell, textAlign: 'left' }}>Description</th>
            {isPaper && <th style={{ ...cell, width: '12%', textAlign: 'center' }}>Size (inch)</th>}
            <th style={{ ...cell, width: '14%', textAlign: 'right' }}>Quantity</th>
            <th style={{ ...cell, width: '14%', textAlign: 'right' }}>Rate{isPaper ? ' / kg' : ''}</th>
            <th style={{ ...cell, width: '16%', textAlign: 'right' }}>Amount ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.LineID}>
              <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
              <td style={cell}>{r.item}{r.GSMID && r.Description ? ` — ${r.Description}` : ''}</td>
              {isPaper && <td style={{ ...cell, textAlign: 'center' }}>{r.WidthInch ? Number(r.WidthInch) : ''}</td>}
              <td style={{ ...cell, textAlign: 'right' }}>{qtyText(r.Qty)} {r.Unit}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{Number(r.UnitPrice) ? fmt(r.UnitPrice) : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{r.amount !== null && r.amount > 0 ? fmt(r.amount) : ''}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }} colSpan={labelSpan}>Total</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{qtyTotal}</td>
            <td style={cell}></td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{allPriced && total > 0 ? fmt(total) : ''}</td>
          </tr>
          {transport > 0 && (
            <tr>
              <td style={{ ...cell, textAlign: 'right' }} colSpan={labelSpan + 2}>Transport cost</td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmt(transport)}</td>
            </tr>
          )}
          {transport > 0 && allPriced && total > 0 && (
            <tr>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }} colSpan={labelSpan + 2}>Grand total</td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{fmt(total + transport)}</td>
            </tr>
          )}
        </tbody>
      </table>
      {isPaper && !allPriced && (
        <div style={{ fontSize: '12px', marginTop: '4px' }}>Rates are per kg; the bill will be made on actual weight received.</div>
      )}

      {terms.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <strong>Terms &amp; Conditions</strong>
          <table style={{ marginTop: '4px' }} cellPadding="0" cellSpacing="0">
            <tbody>
              {terms.map(([k, v]) => (
                <tr key={k}><td style={{ paddingRight: '12px' }}>{k}</td><td>: {v}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {purchase.Remarks && (
        <div style={{ marginTop: '12px', whiteSpace: 'pre-wrap' }}><strong>Remarks:</strong> {purchase.Remarks}</div>
      )}

      <table style={{ marginTop: '60px' }} width="100%" cellSpacing="0" cellPadding="0">
        <tbody>
          <tr>
            <td width="50%" align="left">
              <span style={{ borderTop: '1px solid black', padding: '2px 20px 0', fontSize: '12px' }}>PREPARED BY</span>
            </td>
            <td width="50%" align="right">
              <span style={{ borderTop: '1px solid black', padding: '2px 20px 0', fontSize: '12px' }}>AUTHORISED SIGNATURE</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

const PurchaseOrderPDF = () => {
  const { id } = useParams();
  const [data, setData] = useState(undefined);
  const [saved, setSaved] = useState(null); // { htmlContent, savedAt } of the saved copy, if any
  const [mode, setMode] = useState('fresh'); // 'fresh' = built from the purchase, 'saved' = stored HTML
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [freshKey, setFreshKey] = useState(0);
  const [pad, setPad] = useState(() => {
    try { return localStorage.getItem(padKey) === '1'; } catch (_) { return false; }
  });
  const containerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${window.backendUrl}/purchases/${id}`),
      axios.get(`${window.backendUrl}/purchases/${id}/po`).catch(() => ({ data: { saved: null } })),
    ])
      .then(([detailRes, poRes]) => {
        setData(detailRes.data);
        if (poRes.data.saved) {
          setSaved(poRes.data.saved);
          setMode('saved');
        }
      })
      .catch((err) => { console.error('Error loading purchase:', err); setData(null); });
  }, [id]);

  const togglePad = (on) => {
    setPad(on);
    try { localStorage.setItem(padKey, on ? '1' : '0'); } catch (_) { /* storage blocked */ }
  };

  const handleSave = async () => {
    if (!containerRef.current) return;
    const html = containerRef.current.innerHTML;
    setSaving(true);
    try {
      const res = await axios.post(`${window.backendUrl}/purchases/${id}/po`, { htmlContent: html });
      setSaved({ htmlContent: html, savedAt: res.data.savedAt });
      setEditMode(false);
      setMode('saved');
      setJustSaved(true);
    } catch (err) {
      console.error('Failed to save PO:', err);
      alert(err.response?.data?.error || 'Could not save the PO. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setEditMode(false);
    if (mode === 'saved' && containerRef.current?.firstChild) containerRef.current.firstChild.innerHTML = saved.htmlContent;
    if (mode === 'fresh') setFreshKey((k) => k + 1);
  };

  const handleRegenerate = () => {
    if (!window.confirm('Rebuild this PO from the latest purchase data? Your saved edits stay until you save again.')) return;
    setEditMode(false);
    setJustSaved(false);
    setFreshKey((k) => k + 1);
    setMode('fresh');
  };

  if (data === undefined) return <p style={{ margin: 20 }}>Loading...</p>;
  if (data === null) return <p style={{ margin: 20 }}>Could not load this purchase order.</p>;

  const { purchase, lines } = data;
  // The purchase was edited after this copy was saved, so the saved PO may be out of date
  const savedIsStale = mode === 'saved' && saved?.savedAt && purchase.UpdatedAt && String(purchase.UpdatedAt) > String(saved.savedAt);

  return (
    <div style={{ display: 'block', fontSize: '14px', margin: '0px 20px' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: ${pad ? '0 10mm 8mm' : '10mm'}; }
          .no-print { display: none !important; }
          .po-pad-gap { border: none !important; }
          .po-pad-gap span { display: none !important; }
          .po-edit-mode { outline: none !important; background-color: transparent !important; }
        }
        .po-pad-gap {
          height: ${PAD_GAP}; border: 1px dashed #bbb; display: flex; align-items: center;
          justify-content: center; color: #999; font-size: 12px; margin-bottom: 4px;
        }
        .po-edit-mode { outline: 1px dashed #3b82f6; background-color: rgba(59, 130, 246, 0.04); cursor: text; }
      `}</style>

      <div className="no-print" style={{ margin: '8px 0 6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode((prev) => !prev)}>{editMode ? 'Normal View' : 'Edit Content'}</button>
        {editMode && <button onClick={handleDiscard}>Discard Changes</button>}
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save PO'}</button>
        <button onClick={() => window.print()}>Print</button>
        {mode === 'saved' && <button onClick={handleRegenerate}>Regenerate from latest data</button>}
        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
          <input type="checkbox" checked={pad} onChange={(e) => togglePad(e.target.checked)} />
          Print on letterhead pad (leave top space)
        </label>
      </div>
      <div className="no-print" style={{ margin: '0 0 12px', fontSize: '13px', color: justSaved ? '#198754' : '#555', fontWeight: justSaved ? 'bold' : 'normal' }}>
        {justSaved && '✓ Saved to the Documents tab. '}
        {mode === 'saved'
          ? `Saved copy${saved?.savedAt ? ` from ${String(saved.savedAt).slice(0, 16).replace('T', ' ')}` : ''}.`
          : saved ? 'Rebuilt from the latest data — not saved yet.' : 'Not saved yet — Save PO keeps it in the Documents tab.'}
        {savedIsStale && <span style={{ color: '#b02a37', fontWeight: 'bold' }}> The purchase was edited after this was saved — click "Regenerate from latest data" to update it.</span>}
        {purchase.IsCancelled ? <strong style={{ color: '#b02a37' }}> This purchase is cancelled.</strong> : null}
      </div>

      <div style={{ maxWidth: '800px' }}>
        {pad && <div className="po-pad-gap"><span>Letterhead pad area — left blank when printing</span></div>}
        <div
          ref={containerRef}
          className={editMode ? 'po-edit-mode' : ''}
          contentEditable={editMode}
          suppressContentEditableWarning
        >
          {mode === 'saved'
            ? <div dangerouslySetInnerHTML={{ __html: saved.htmlContent }} />
            : <FreshPo key={`${freshKey}-${pad}`} purchase={purchase} lines={lines} pad={pad} />}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderPDF;
